import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { pricingConfig, calculatePricing as calculatePricingUtil, formatCurrency as formatCurrencyUtil, 
  // getInvoiceVolumeCaption, 
  getPerCompanyInvoiceVolume, calculateInvoiceFeaturePrice, calculateIdCardFeaturePrice, calculateSmartIdFeaturePrice, calculatePremiumSupportPrice } from "../utils/pricingUtils";

const PricingCalculationPage = ({ data, onUpdate }) => {
  const [billingPeriod, setBillingPeriod] = useState('yearly');
  // const [email, setEmail] = useState('');
  // const [isSubmitting, setIsSubmitting] = useState(false);
  // const [submitStatus, setSubmitStatus] = useState({ text: '', type: '' });
  // const [fieldErrors, setFieldErrors] = useState({
  //   email: ''
  // });

  // Use shared pricing configuration (imported from pricingUtils)

  // Calculate pricing based on wizard data (using shared utility)
  const calculatePricing = () => {
    return calculatePricingUtil(data, billingPeriod);
  };

  // Use shared helper functions (imported from pricingUtils) - they're used by getDiscountPercentage and calculateMonthlyEquivalent

  // Get discount percentage for a specific billing period compared to monthly
  const getDiscountPercentage = (period) => {
    if (period === 'monthly') {
      return 0; // No discount for monthly billing
    }

    const companyCount = data.selectedOption && ['1', '2', '3', '4', '5'].includes(data.selectedOption)
      ? parseInt(data.selectedOption)
      : parseInt(data.companyCount) || 1;

    // Calculate monthly equivalent
    const monthlyBase = pricingConfig.base.monthly;
    let monthlyPerCompanyFeatures = 0;
    let monthlyPerConfigurationFeatures = 0;

    // Check if both Smart-ID and ID card are selected
    const hasSmartId = data.selectedFeatures.includes('digital-signing-smart-id');
    const hasIdCard = data.selectedFeatures.includes('digital-signing-id-card');
    const bothSigningSelected = hasSmartId && hasIdCard;

    // Identify signing features (per-configuration)
    const signingFeatures = ['digital-signing-id-card', 'digital-signing-smart-id'];

    // Add ERP integration if user selected "with existing system"
    if (data.usageType === 'with-existing') {
      monthlyPerCompanyFeatures += pricingConfig.features['erp-integration'].monthly;
    }

    // Add premium support if user selected it or if using existing system
    if (data.supportType === 'premium-support' || data.usageType === 'with-existing') {
      monthlyPerCompanyFeatures += calculatePremiumSupportPrice(data, 'monthly', companyCount);
    }

    data.selectedFeatures.forEach(feature => {
      if (pricingConfig.features[feature]) {
        let featurePrice = pricingConfig.features[feature].monthly;

        // Apply volume-based pricing for invoice features
        if (feature === 'electronic-invoicing' && data.customerInvoiceCount) {
          // For per-company features, divide total invoice count by company count
          const perCompanyInvoiceCount = getPerCompanyInvoiceVolume(data.customerInvoiceCount, companyCount);
          featurePrice = calculateInvoiceFeaturePrice(perCompanyInvoiceCount, 'monthly', 'customer');
        } else if (feature === 'invoice-processing' && data.supplierInvoiceCount) {
          // For per-company features, divide total invoice count by company count
          const perCompanyInvoiceCount = getPerCompanyInvoiceVolume(data.supplierInvoiceCount, companyCount);
          featurePrice = calculateInvoiceFeaturePrice(perCompanyInvoiceCount, 'monthly', 'supplier');
        } else if (feature === 'digital-signing-id-card' && data.supplierInvoiceCount) {
          if (bothSigningSelected) {
            // ID card is included for free when both are selected
            featurePrice = 0;
          } else {
            featurePrice = calculateIdCardFeaturePrice(data.supplierInvoiceCount, 'monthly');
          }
        } else if (feature === 'digital-signing-smart-id' && data.supplierInvoiceCount) {
          featurePrice = calculateSmartIdFeaturePrice(data.supplierInvoiceCount, 'monthly');
        }

        // Separate per-company and per-configuration features
        if (signingFeatures.includes(feature)) {
          monthlyPerConfigurationFeatures += featurePrice;
        } else {
          monthlyPerCompanyFeatures += featurePrice;
        }
      }
    });

    const monthlyPerCompanySubtotal = monthlyBase + monthlyPerCompanyFeatures;
    const monthlyPerCompanyTotal = monthlyPerCompanySubtotal * companyCount;

    // Calculate bulk discount for monthly (only on per-company features)
    let monthlyBulkDiscount = 0;
    if (companyCount > 1) {
      const monthlyBulkDiscountPercentage = Math.min((companyCount - 1) * pricingConfig.bulkDiscount.discountPerCompany, pricingConfig.bulkDiscount.maxDiscount);
      monthlyBulkDiscount = (monthlyPerCompanySubtotal * companyCount * monthlyBulkDiscountPercentage) / 100;
    }
    const monthlyFinal = monthlyPerCompanyTotal - monthlyBulkDiscount + monthlyPerConfigurationFeatures;

    // Calculate period equivalent
    const periodMultiplier = period === 'quarterly' ? 3 : period === 'biyearly' ? 6 : 12;
    const periodEquivalent = monthlyFinal * periodMultiplier;

    // Calculate actual period price
    const periodBase = pricingConfig.base[period];
    let periodPerCompanyFeatures = 0;
    let periodPerConfigurationFeatures = 0;

    // Add ERP integration if user selected "with existing system"
    if (data.usageType === 'with-existing') {
      periodPerCompanyFeatures += pricingConfig.features['erp-integration'][period];
    }

    // Add premium support if user selected it or if using existing system
    if (data.supportType === 'premium-support' || data.usageType === 'with-existing') {
      periodPerCompanyFeatures += calculatePremiumSupportPrice(data, period, companyCount);
    }

    data.selectedFeatures.forEach(feature => {
      if (pricingConfig.features[feature]) {
        let featurePrice = pricingConfig.features[feature][period];

        // Apply volume-based pricing for invoice features
        if (feature === 'electronic-invoicing' && data.customerInvoiceCount) {
          // For per-company features, divide total invoice count by company count
          const perCompanyInvoiceCount = getPerCompanyInvoiceVolume(data.customerInvoiceCount, companyCount);
          featurePrice = calculateInvoiceFeaturePrice(perCompanyInvoiceCount, period, 'customer');
        } else if (feature === 'invoice-processing' && data.supplierInvoiceCount) {
          // For per-company features, divide total invoice count by company count
          const perCompanyInvoiceCount = getPerCompanyInvoiceVolume(data.supplierInvoiceCount, companyCount);
          featurePrice = calculateInvoiceFeaturePrice(perCompanyInvoiceCount, period, 'supplier');
        } else if (feature === 'digital-signing-id-card' && data.supplierInvoiceCount) {
          if (bothSigningSelected) {
            // ID card is included for free when both are selected
            featurePrice = 0;
          } else {
            featurePrice = calculateIdCardFeaturePrice(data.supplierInvoiceCount, period);
          }
        } else if (feature === 'digital-signing-smart-id' && data.supplierInvoiceCount) {
          featurePrice = calculateSmartIdFeaturePrice(data.supplierInvoiceCount, period);
        }

        // Separate per-company and per-configuration features
        if (signingFeatures.includes(feature)) {
          periodPerConfigurationFeatures += featurePrice;
        } else {
          periodPerCompanyFeatures += featurePrice;
        }
      }
    });

    const periodPerCompanySubtotal = periodBase + periodPerCompanyFeatures;
    const periodPerCompanyTotal = periodPerCompanySubtotal * companyCount;

    // Calculate bulk discount for period (only on per-company features)
    let periodBulkDiscount = 0;
    if (companyCount > 1) {
      const periodBulkDiscountPercentage = Math.min((companyCount - 1) * pricingConfig.bulkDiscount.discountPerCompany, pricingConfig.bulkDiscount.maxDiscount);
      periodBulkDiscount = (periodPerCompanySubtotal * companyCount * periodBulkDiscountPercentage) / 100;
    }
    const periodFinal = periodPerCompanyTotal - periodBulkDiscount + periodPerConfigurationFeatures;

    // Calculate discount
    const discount = ((periodEquivalent - periodFinal) / periodEquivalent) * 100;

    return Math.round(discount);
  };

  // Update parent component when data changes
  useEffect(() => {
    onUpdate({
      billingPeriod,
      // email
    });
  }, [billingPeriod
    // , email
  ]);

  // Handle billing period change
  const handleBillingPeriodChange = (period) => {
    setBillingPeriod(period);
  };

  // // Handle email change
  // const handleEmailChange = (e) => {
  //   const value = e.target.value;
  //   setEmail(value);
  //   setFieldErrors(prev => ({
  //     ...prev,
  //     email: ''
  //   }));
  // };

  // // Validate email
  // const validateEmail = (email) => {
  //   const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  //   if (!email || email.trim() === '') {
  //     return 'Email is required';
  //   }
  //   if (!emailRegex.test(email)) {
  //     return 'Please enter a valid email address';
  //   }
  //   return '';
  // };

  // // Handle email submission
  // const handleEmailSubmit = async (e) => {
  //   e.preventDefault();

  //   const emailError = validateEmail(email);
  //   if (emailError) {
  //     setFieldErrors(prev => ({
  //       ...prev,
  //       email: emailError
  //     }));
  //     return;
  //   }

  //   setIsSubmitting(true);
  //   setSubmitStatus({ text: 'Sending pricing information...', type: 'info' });

  //   try {
  //     // TODO: Replace with actual backend endpoint
  //     const response = await fetch('/api/onboarding/send-pricing', {
  //       method: 'POST',
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify({
  //         email,
  //         billingPeriod,
  //         pricing,
  //         wizardData: data
  //       })
  //     });

  //     if (response.ok) {
  //       setSubmitStatus({
  //         text: '✅ Pricing information sent successfully! Check your email.',
  //         type: 'success'
  //       });
  //     } else {
  //       throw new Error('Failed to send pricing information');
  //     }
  //   } catch (error) {
  //     setSubmitStatus({
  //       text: '❌ Sorry, there was an error sending the pricing information. Please try again.',
  //       type: 'error'
  //     });
  //   } finally {
  //     setIsSubmitting(false);
  //   }
  // };

  // Use shared formatCurrency function
  const formatCurrency = formatCurrencyUtil;

  // Get billing period label
  const getBillingPeriodLabel = (period) => {
    return pricingConfig.billingPeriodLabels[period] || period;
  };

  // Calculate monthly equivalent for savings comparison
  const calculateMonthlyEquivalent = () => {
    const companyCount = data.selectedOption && ['1', '2', '3', '4', '5'].includes(data.selectedOption)
      ? parseInt(data.selectedOption)
      : parseInt(data.companyCount) || 1;

    let monthlyBase = pricingConfig.base.monthly;
    let monthlyPerCompanyFeatures = 0;
    let monthlyPerConfigurationFeatures = 0;

    // Check if both Smart-ID and ID card are selected
    const hasSmartId = data.selectedFeatures.includes('digital-signing-smart-id');
    const hasIdCard = data.selectedFeatures.includes('digital-signing-id-card');
    const bothSigningSelected = hasSmartId && hasIdCard;

    // Identify signing features (per-configuration)
    const signingFeatures = ['digital-signing-id-card', 'digital-signing-smart-id'];

    // Add ERP integration if user selected "with existing system"
    if (data.usageType === 'with-existing') {
      monthlyPerCompanyFeatures += pricingConfig.features['erp-integration'].monthly;
    }

    // Add premium support if user selected it or if using existing system
    if (data.supportType === 'premium-support' || data.usageType === 'with-existing') {
      monthlyPerCompanyFeatures += calculatePremiumSupportPrice(data, 'monthly', companyCount);
    }

    // Calculate monthly features with same volume-based pricing
    data.selectedFeatures.forEach(feature => {
      if (pricingConfig.features[feature]) {
        let featurePrice = pricingConfig.features[feature].monthly;

        // Apply volume-based pricing for invoice features
        if (feature === 'electronic-invoicing' && data.customerInvoiceCount) {
          // For per-company features, divide total invoice count by company count
          const perCompanyInvoiceCount = getPerCompanyInvoiceVolume(data.customerInvoiceCount, companyCount);
          featurePrice = calculateInvoiceFeaturePrice(perCompanyInvoiceCount, 'monthly', 'customer');
        } else if (feature === 'invoice-processing' && data.supplierInvoiceCount) {
          // For per-company features, divide total invoice count by company count
          const perCompanyInvoiceCount = getPerCompanyInvoiceVolume(data.supplierInvoiceCount, companyCount);
          featurePrice = calculateInvoiceFeaturePrice(perCompanyInvoiceCount, 'monthly', 'supplier');
        } else if (feature === 'digital-signing-id-card' && data.supplierInvoiceCount) {
          if (bothSigningSelected) {
            // ID card is included for free when both are selected
            featurePrice = 0;
          } else {
            featurePrice = calculateIdCardFeaturePrice(data.supplierInvoiceCount, 'monthly');
          }
        } else if (feature === 'digital-signing-smart-id' && data.supplierInvoiceCount) {
          featurePrice = calculateSmartIdFeaturePrice(data.supplierInvoiceCount, 'monthly');
        }

        // Separate per-company and per-configuration features
        if (signingFeatures.includes(feature)) {
          monthlyPerConfigurationFeatures += featurePrice;
        } else {
          monthlyPerCompanyFeatures += featurePrice;
        }
      }
    });

    // Calculate monthly subtotal for per-company features
    let monthlyPerCompanySubtotal = monthlyBase + monthlyPerCompanyFeatures;

    // Apply bulk discount for monthly (only on per-company features)
    let monthlyBulkDiscount = 0;
    if (companyCount > 1) {
      const monthlyBulkDiscountPercentage = Math.min((companyCount - 1) * pricingConfig.bulkDiscount.discountPerCompany, pricingConfig.bulkDiscount.maxDiscount);
      monthlyBulkDiscount = (monthlyPerCompanySubtotal * companyCount * monthlyBulkDiscountPercentage) / 100;
    }

    // Calculate monthly total: (per-company subtotal * companyCount - bulk discount) + per-configuration features
    let monthlyTotal = (monthlyPerCompanySubtotal * companyCount) - monthlyBulkDiscount + monthlyPerConfigurationFeatures;

    // Convert to period equivalent
    const periodMultiplier = billingPeriod === 'quarterly' ? 3 : billingPeriod === 'biyearly' ? 6 : 12;
    return monthlyTotal * periodMultiplier;
  };

  const pricing = useMemo(() => calculatePricing(), [data, billingPeriod]);

  return (
    <div className="wizard-page pricing-calculation-page">
      <div className="page-header">
        <h2>Your pricing</h2>
        <p>Based on your configuration, here's your personalized pricing breakdown.</p>
      </div>

      <div className="form-content">
        {/* Billing Period Selector */}
        <div className="billing-period-section">
          <h3>Billing period</h3>
          <div className="billing-options">
            {Object.keys(pricingConfig.base).map(period => (
              <button
                key={period}
                type="button"
                className={`billing-option ${billingPeriod === period ? 'selected' : ''}`}
                onClick={() => handleBillingPeriodChange(period)}
              >
                <div className="billing-label">{getBillingPeriodLabel(period)}</div>
                {period !== 'monthly' && getDiscountPercentage(period) > 0 && (
                  <div className="discount-badge">Save {getDiscountPercentage(period)}%</div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Pricing Breakdown */}
        <div className="pricing-breakdown">
          <h3>Pricing breakdown</h3>
          <div className="pricing-card">
            <div className="pricing-item">
              <span className="pricing-label">Base plan ({getBillingPeriodLabel(billingPeriod).toLowerCase()})</span>
              <span className="pricing-value">{formatCurrency(pricing.basePrice)}</span>
            </div>

            {pricing.featuresBreakdown.length > 0 && (
              <>
                {pricing.featuresBreakdown
                  .filter(feature => !['digital-signing-id-card', 'digital-signing-smart-id'].includes(feature.feature))
                  .map((feature, index) => (
                    <div key={index} className="pricing-item feature-item">
                      <span className="pricing-label">
                        {pricingConfig.featureLabels[feature.feature]}
                        {(feature.volumeBased || feature.isAutoIncluded) && (
                          <span className="volume-caption">
                            {feature.feature === 'erp-integration'
                              ? ` (${feature.volumeCaption})`
                              : feature.feature === 'premium-support'
                              ? feature.isAutoIncluded
                                ? ' (auto-included)'
                                : ''
                              : ` (up to ${feature.volumeCaption} invoices)`
                            }
                          </span>
                        )}
                      </span>
                      <span className="pricing-value">{formatCurrency(feature.price)}</span>
                    </div>
                  ))}
              </>
            )}

            {pricing.companyCount > 1 && (
              <>
                <div className="pricing-item pricing-subtotal">
                  <span className="pricing-label">Subtotal per company</span>
                  <span className="pricing-value">{formatCurrency(pricing.perCompanySubtotal)}</span>
                </div>
                <div className="pricing-item pricing-subtotal">
                  <span className="pricing-label">Total for {pricing.companyCount} companies</span>
                  <span className="pricing-value">{formatCurrency(pricing.perCompanySubtotal * pricing.companyCount)}</span>
                </div>
                {pricing.bulkDiscountPercentage > 0 && (
                  <div className="pricing-item discount-item">
                    <span className="pricing-label">Bulk discount ({pricing.bulkDiscountPercentage}%)</span>
                    <span className="pricing-value">-{formatCurrency(pricing.bulkDiscountAmount)}</span>
                  </div>
                )}
              </>
            )}

            {pricing.featuresBreakdown
              .filter(feature => ['digital-signing-id-card', 'digital-signing-smart-id'].includes(feature.feature))
              .map((feature, index) => (
                <div key={`config-${index}`} className="pricing-item feature-item">
                  <span className="pricing-label">
                    {pricingConfig.featureLabels[feature.feature]}
                    {(feature.volumeBased || feature.isAutoIncluded) && (
                      <span className="volume-caption">
                        {feature.feature === 'digital-signing-smart-id' 
                          ? ` (${Math.max(pricingConfig.features['digital-signing-smart-id'].baseAmount, parseInt(feature.volumeCaption))} signatures/month)`
                          : feature.feature === 'digital-signing-id-card'
                          ? feature.isIncluded
                            ? ` (${Math.max(pricingConfig.features['digital-signing-smart-id'].baseAmount, parseInt(feature.volumeCaption))} signatures/month - included)`
                            : ` (${Math.max(pricingConfig.features['digital-signing-id-card'].baseAmount, parseInt(feature.volumeCaption))} signatures/month)`
                          : ''
                        }
                      </span>
                    )}
                  </span>
                  <span className="pricing-value">{formatCurrency(feature.price)}</span>
                </div>
              ))}


            <div className="pricing-total">
              <span className="total-label">Total ({getBillingPeriodLabel(billingPeriod).toLowerCase()})</span>
              <span className="total-value">{formatCurrency(pricing.totalPrice)}</span>
            </div>

            {pricing.companyCount >= 10 && (
              <div className="personalized-discount-banner">
                <span className="banner-text">
                  💼 You might be eligible for personalized discounts! Get in touch with us for a custom quote.
                </span>
              </div>
            )}

            {billingPeriod !== 'monthly' && getDiscountPercentage(billingPeriod) > 0 && (
              <div className="savings-info">
                <span className="savings-text">
                  You save {formatCurrency(calculateMonthlyEquivalent() - pricing.totalPrice)} compared to monthly billing
                </span>
              </div>
            )}

            <div className="pricing-footer">
              Prices are exclusive of VAT and any local taxes or duties.
            </div>
          </div>

        </div>


        {/* Email Form */}
        {/* <div className="email-section">
          <h3>Get your pricing details</h3>

          <form onSubmit={handleEmailSubmit} className="email-form">
            <div className="form-group">
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="Enter your email address"
                className={`form-input ${fieldErrors.email ? 'error' : ''}`}
                required
              />
              {fieldErrors.email && (
                <span className="field-error">{fieldErrors.email}</span>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary email-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send pricing details'}
            </button>
          </form>

          {submitStatus.text && (
            <div className={`status ${submitStatus.type}`}>
              {submitStatus.text}
            </div>
          )}
        </div> */}

      </div>
    </div>
  );
};

PricingCalculationPage.propTypes = {
  data: PropTypes.shape({
    usageType: PropTypes.string,
    existingSystem: PropTypes.string,
    selectedFeatures: PropTypes.array,
    companyCount: PropTypes.string,
    selectedOption: PropTypes.string,
    invoiceVolume: PropTypes.string,
    customerInvoiceCount: PropTypes.string,
    supplierInvoiceCount: PropTypes.string,
    supportType: PropTypes.string
  }).isRequired,
  onUpdate: PropTypes.func.isRequired
};

export default PricingCalculationPage;
